import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { type AccountInfoData } from "../../AccountSelection/AccountSelectionTabelView/api/GetAccountList";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import { Col, Row } from "../../common/components/Layout";
import { Link } from "../../common/components/Link";
import { MaterialInput } from "../../common/components/materialInput/MaterialInput";
import Modal from "../../common/components/Modal";
import { useBooleanState } from "../../common/hooks/useBooleanState";
import DropdownWithMultiSelect from "../../UserAccounts/DropdownWithMultiselect/DropdownWithMultiSelect";
import { EMAIL_PATTERN } from "../../utils/constants";
import { makeAccountDataIntoMultiSelect } from "../helper/makeAccountData";
import { dropdownAccountSelectionToAccountData } from "../helper/dropdownAccountSelectionToData";
import { type DefaultEmailOrderQuoteData, type EmailOrderQuoteData } from "../types";
import { selectedOrderQuoteAccountDropdown } from "../helper/dropdownDefaultSelectionList";
import { userCanSetMyCompanyOrders, userCanSetMyCompanyQuotes } from "../../AccountSettings/helper/permissions";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { Stacker } from "~/common/components/Stacker/Stacker";

interface EmailOrderQuotePopupProps {
  availableAccounts?: AccountInfoData[];
  onSubmit?: (data: EmailOrderQuoteData) => void;
  onDelete?: (data: EmailOrderQuoteData) => void;
}
export const useEmailOrderQuotePopup = ({ availableAccounts, onSubmit, onDelete }: EmailOrderQuotePopupProps) => {
  const { setTrue: openPopup, setFalse: closePopup, val: isModalOpen } = useBooleanState(false);
  const { setTrue: enableEdit, setFalse: disableEdit, val: isEditMode } = useBooleanState(false);

  // empty model data
  const emptyData: EmailOrderQuoteData = {
    recipientEmail: "",
    orderAcknowledgement: [],
    quoteAcknowledgement: [],
  };
  const [emailData, setEmailData] = useState<EmailOrderQuoteData>(emptyData);

  // form model data
  const emptyDefaultData: DefaultEmailOrderQuoteData = {
    recipientEmail: "",
    orderAcknowledgement: "",
    quoteAcknowledgement: "",
  };

  const {
    control,
    getValues,
    formState: { errors },
    watch,
    setValue,
    trigger,
  } = useForm<DefaultEmailOrderQuoteData>({
    mode: "onBlur",
    defaultValues: {
      ...emptyDefaultData,
    },
  });

  const setFormDefaultData = () => {
    if (!isEditMode) {
      return;
    }
    setValue("recipientEmail", emailData.recipientEmail);
    setValue("orderAcknowledgement", selectedOrderQuoteAccountDropdown(orderAccountsDropdown));
    setValue("quoteAcknowledgement", selectedOrderQuoteAccountDropdown(quoteAccountsDropdown));
    // force form for validation
    trigger();
  };

  useEffect(setFormDefaultData, [emailData]);

  const { t } = useTranslation();
  const [submitData, setSubmitData] = useState<EmailOrderQuoteData>();

  const onChangeForm = () => {
    if (Object.keys(errors).length > 0) {
      return;
    }

    const payload: EmailOrderQuoteData = {
      recipientEmail: getValues("recipientEmail"),
      orderAcknowledgement: dropdownAccountSelectionToAccountData(
        getValues("orderAcknowledgement") ?? "",
        availableAccounts,
      ),
      quoteAcknowledgement: dropdownAccountSelectionToAccountData(
        getValues("quoteAcknowledgement") ?? "",
        availableAccounts,
      ),
    };

    setSubmitData(payload);
  };

  useEffect(onChangeForm, [watch("recipientEmail"), watch("orderAcknowledgement"), watch("quoteAcknowledgement")]);

  // manage popup open
  const open = (emailOrderQuoteData?: EmailOrderQuoteData) => {
    if (emailOrderQuoteData) {
      // EDIT MODE
      setEmailData(emailOrderQuoteData);
      enableEdit();
    } else {
      // NEW MODE
      setEmailData(emptyData);
      disableEdit();
    }
    openPopup();
  };

  // reset on close
  const close = () => {
    setEmailData(emptyData);
    closePopup();
  };

  const isDisabled =
    Object.keys(errors).length > 0 || !getValues("recipientEmail") || getValues("recipientEmail") === "";

  const orderAccountsDropdown = useMemo(
    () =>
      makeAccountDataIntoMultiSelect({ list: availableAccounts ?? [], email: emailData.orderAcknowledgement ?? [] }),
    [availableAccounts, emailData.orderAcknowledgement],
  );
  const quoteAccountsDropdown = useMemo(
    () =>
      makeAccountDataIntoMultiSelect({ list: availableAccounts ?? [], email: emailData.quoteAcknowledgement ?? [] }),
    [availableAccounts, emailData.quoteAcknowledgement],
  );

  const errorMessage = "error.invalidEmail";

  const modal = isModalOpen && (
    <Modal
      onClose={close}
      title={isEditMode ? t("page.orderQuoteSettings.modal.title.edit") : t("page.orderQuoteSettings.modal.title.add")}
      footerChildren={
        <div className="d-flex flex-justify-between flex-align-center w-100">
          <p>{t("page.orderQuoteSettings.modal.label.mandatory")}</p>
          <Stacker direction={"row"} gap={8}>
            <Button variant={"text"} data-test-id={"address-modal-btn-cancel"} onClick={close}>
              {t("page.orderQuoteSettings.modal.button.cancel")}
            </Button>
            <Button
              data-test-id={"address-modal-btn-save"}
              className="ml-30"
              disabled={isDisabled}
              onClick={() => {
                !isDisabled && submitData && onSubmit && onSubmit(submitData);
                close();
              }}
            >
              {t(isEditMode ? "page.orderQuoteSettings.modal.button.save" : "page.orderQuoteSettings.modal.button.add")}
            </Button>
          </Stacker>
        </div>
      }
    >
      <div className="scha__order-quote-settings__popup">
        <div className="scha__order-quote-settings__popup__body">
          <Row className={"scha__order-quote-settings__popup__body__row__email mb-3"}>
            <Col size={12}>
              <Controller
                name="recipientEmail"
                defaultValue={emailData?.recipientEmail}
                control={control}
                render={({ field: { value, onChange, onBlur } }) => (
                  <MaterialInput
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    label={t("page.orderQuoteSettings.modal.label.email")}
                    error={!!errors.recipientEmail}
                    colorHelperOnError={true}
                    helperLabel={t(errorMessage)}
                    disabled={isEditMode}
                  />
                )}
                rules={{
                  required: { value: true, message: errorMessage },
                  pattern: { value: EMAIL_PATTERN, message: errorMessage },
                }}
              />
            </Col>
          </Row>
          <Row className={"scha__order-quote-settings__popup__body__row__copy mb-3"}>
            <Col size={12} className={"scha__order-quote-settings__popup__body__row__copy__label"}>
              {t("page.orderQuoteSettings.modal.label.copy")}
            </Col>
          </Row>
          <Row className={"scha__order-quote-settings__popup__body__row__accounts " + (isEditMode ? "mb-3" : "mb-30")}>
            {userCanSetMyCompanyOrders && (
              <Col size={12} className={"mb-3"}>
                <p className={"mb-1"}>{t("page.orderQuoteSettings.modal.label.order")}</p>
                <Controller
                  name="orderAcknowledgement"
                  defaultValue={selectedOrderQuoteAccountDropdown(orderAccountsDropdown)}
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <DropdownWithMultiSelect
                      items={orderAccountsDropdown}
                      value={value ? value.split(",") : []}
                      label={t("page.orderQuoteSettings.modal.label.account")}
                      onChange={(e) => onChange((e.target.value as string[]).join(","))}
                      onBlur={onBlur}
                      data-test-id="email-order-popup-quoiteAccountMultiselect"
                      className={"scha__dropdown-multi-select__email-order-quote-account-selector"}
                    />
                  )}
                />
              </Col>
            )}
            {userCanSetMyCompanyQuotes && (
              <Col size={12} className={"mb-3"}>
                <p className={"mb-1"}>{t("page.orderQuoteSettings.modal.label.quote")}</p>
                <Controller
                  name="quoteAcknowledgement"
                  defaultValue={selectedOrderQuoteAccountDropdown(quoteAccountsDropdown)}
                  control={control}
                  render={({ field: { value, onChange, onBlur } }) => (
                    <DropdownWithMultiSelect
                      items={quoteAccountsDropdown}
                      value={value ? value.split(",") : []}
                      label={t("page.orderQuoteSettings.modal.label.account")}
                      onChange={(e) => onChange((e.target.value as string[]).join(","))}
                      onBlur={onBlur}
                      data-test-id="email-order-popup-quoiteAccountMultiselect"
                      className={"scha__dropdown-multi-select__email-order-quote-account-selector"}
                    />
                  )}
                />
              </Col>
            )}
          </Row>
          {isEditMode && (
            <Row className={"mb-30"}>
              <Col size={12}>
                <Link onClick={() => onDelete && onDelete(emailData)} iconBefore={<Icon type={IconType.Bin} />}>
                  {t("page.orderQuoteSettings.table.button.delete")}
                </Link>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </Modal>
  );

  return { modal, open, close, visible: isModalOpen } as const;
};
